import { downloadCSV } from "@/utils/downloadCSV";
import { formatCurrency } from "@/utils/formatCurrency";

// Helper function for CSV export
export const exportToCSV = (data: any[], filename: string) => {
  if (!data.length) return;
  const headers = Object.keys(data[0] || {});
  downloadCSV(data, filename, headers);
};

// Helper functions to reduce code duplication
export const calculateTrend = (
  percentChange: number | undefined,
  defaultValue: string = "+2.7%"
) => {
  if (percentChange === undefined) return defaultValue;
  return (percentChange > 0 ? "+" : "") + percentChange.toFixed(1) + "%";
};

export const calculateTrendColor = (
  percentChange: number | undefined,
  defaultColor: "green" | "red" = "green"
): "green" | "red" => {
  if (percentChange === undefined) return defaultColor;
  return percentChange > 0 ? "green" : percentChange < 0 ? "red" : "green";
};

export const getTrendColorConfig = (trendColor?: "green" | "red") => {
  let trendBg = "transparent";
  let trendText = "#009688";

  switch (trendColor) {
    case "green":
      trendBg = "#E6F4F1";
      trendText = "#009688";
      break;
    case "red":
      trendBg = "#FFEDE6";
      trendText = "#B53700";
      break;
    default:
      break;
  }

  return { trendBg, trendText };
};

// Tooltip styling configuration for stat cards
export const getStatCardTooltipStyles = () => ({
  backgroundColor: "#001E4C",
  color: "#FFFFFF",
  fontSize: "10px",
  padding: "8px 12px",
  borderRadius: "8px",
  border: "none",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  maxWidth: "280px",
  minWidth: "200px",
  "& .MuiTooltip-arrow": {
    color: "#001E4C",
  },
});

// Dashboard statistic interface
export interface DashboardStatistic {
  admins: number;
  oems: number;
  contractors: number;
  totalUsers: number;
  percentages: {
    admins: number;
    oems: number;
    contractors: number;
  };
}

// Tooltip configuration for donut charts
export const getDonutChartTooltipConfig = (
  data: any[],
  total: number,
  valueField: string,
  labelField: string,
  isCurrency: boolean = false,
  isMWh: boolean = false
) => ({
  backgroundColor: "#001E4C",
  titleColor: "#FFFFFF",
  bodyColor: "#FFFFFF",
  borderColor: "#001E4C",
  borderWidth: 0,
  cornerRadius: 8,
  displayColors: false,
  padding: 12,
  titleFont: {
    size: 12,
    weight: "normal" as const,
  },
  bodyFont: {
    size: 14,
    weight: "bold" as const,
  },
  callbacks: {
    title: function (tooltipItems: any) {
      const tooltipItem = tooltipItems[0];
      const dataIndex = tooltipItem.dataIndex;
      const item = data[dataIndex];
      if (!item) return "Unknown";

      const value = item[valueField];
      const percent = total ? Math.round((value / total) * 100) : 0;

      return `${item[labelField]} (${percent}%)`;
    },
    label: function (tooltipItem: any) {
      const dataIndex = tooltipItem.dataIndex;
      const item = data[dataIndex];
      if (!item) return "";

      const value = item[valueField];
      let formattedValue;

      if (isCurrency) {
        formattedValue = formatCurrency(typeof value === "number" ? value : 0);
      } else if (isMWh) {
        formattedValue = `${(typeof value === "number"
          ? value
          : 0
        ).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} MWh`;
      } else {
        formattedValue = (
          typeof value === "number" ? value : 0
        ).toLocaleString();
      }

      return formattedValue;
    },
  },
});

/**
 * The manufacturer label for an OEM slice or table row (SDCP-1308).
 *
 * The dashboard used to fall back to `OEM ${oemId}` whenever the OEM partner account had no
 * company name — which in production is every one of them, giving "OEM 39" / "OEM 42". The API
 * now resolves a real name (company name, else the project's battery manufacturer), so the UI
 * reads that and never renders an account id.
 */
export const getOemLabel = (item: {
  oemName?: string | null;
  OEM?: { companyName?: string | null } | null;
}): string =>
  item?.oemName?.trim() ||
  item?.OEM?.companyName?.trim() ||
  "Unknown manufacturer";

/**
 * Reads a KPI figure that the API has published in two shapes over time (SDCP-1308).
 *
 * Most KPI cards receive `{ value, percentChange }`, but `totalAmountPaid` is a plain number.
 * A naive `x?.value || x` turns a legitimate 0 into the wrapper object and then NaN, so the
 * shape is checked explicitly.
 */
export const readKpiValue = (
  kpi: number | { value?: number | string } | null | undefined
): number => {
  const raw = typeof kpi === "object" && kpi !== null ? kpi.value : kpi;
  const value = Number(raw);
  return Number.isFinite(value) ? value : 0;
};

/**
 * A contractor row's share of the incentive total across all contractors (SDCP-1308).
 *
 * The "% of Total Incentives" column was hardcoded to "0%" for every row. It is the row's
 * totalIncentives over the sum across all contractors, so the column sums to ~100%. Returns a
 * formatted string (e.g. "13.7%"); a zero or missing total yields "0%" rather than NaN.
 */
export const formatSharePercent = (
  value: number | string | null | undefined,
  total: number | string | null | undefined
): string => {
  const v = Number(value);
  const t = Number(total);
  if (!Number.isFinite(v) || !Number.isFinite(t) || t <= 0) return "0%";
  return `${((v / t) * 100).toFixed(1)}%`;
};

/**
 * A Customer Type Breakdown table row (SDCP-1308).
 *
 * Two bugs: the label was `rateType` alone, so NEM and None rows of the same rate type looked
 * like duplicates; and the count was placed into a column by tariffType, so a "Market Rate" row
 * with tariff "None" matched no branch and rendered an empty 0/0 row. The label now carries the
 * tariff, and the count keys off the rate type alone — CARE/FERA is Non-Market, everything else
 * is Market — so every row's count lands in exactly one column.
 */
export const buildCustomerTypeRow = (item: {
  tariffType?: string | null;
  rateType?: string | null;
  count?: number | string | null;
}): { fullName: string; marketRate: number; nonMarketRate: number } => {
  const count = Number(item?.count) || 0;
  const rateType = item?.rateType || "Unknown";
  const tariffType = item?.tariffType || "Unknown";
  const isNonMarket =
    rateType.includes("CARE") || rateType.includes("FERA");
  return {
    fullName: `${tariffType} - ${rateType}`,
    marketRate: isNonMarket ? 0 : count,
    nonMarketRate: isNonMarket ? count : 0,
  };
};
